import { action, makeObservable, observable } from 'mobx';

/**
 * Shared top-search state. NavBar writes; Notes observes and runs the search.
 * `sequence` is bumped on every update so observers react even when the same
 * query is submitted twice (location.search alone wouldn't change).
 */
export class SearchService {
  @observable query = '';
  @observable sequence = 0;
  // Bumped to ask the top search box to take focus (e.g. from the "Search" rail item)
  @observable focusRequest = 0;
  // Whether semantic (vector) search is usable — set once from /api/note/search-config
  @observable semanticAvailable = false;
  // User's choice; when on, the search also runs a vector pass
  @observable useSemantic = false;

  constructor() {
    makeObservable(this);
  }

  /** Ask the top search box to take focus. */
  @action
  focus(): void {
    this.focusRequest++;
  }

  get isActive(): boolean {
    return this.query.trim().length > 0;
  }

  @action
  setSemanticAvailable(available: boolean): void {
    this.semanticAvailable = available;
    if (!available) this.useSemantic = false;
  }

  /** Flip exact/semantic; flipping re-runs the active query via the sequence bump. */
  @action
  toggleSemantic(): void {
    if (!this.semanticAvailable) return;
    this.useSemantic = !this.useSemantic;
    if (this.isActive) this.sequence++;
  }

  @action
  update(query: string): void {
    this.query = query;
    this.sequence++;
  }

  @action
  clear(): void {
    if (!this.isActive) return;
    this.query = '';
    this.sequence++;
  }

  /** Consume a ?q= deep link: seed the query, strip the param from the URL. */
  @action
  consumeQueryParam(): void {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q === null) return;
    this.update(q);
    params.delete('q');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }
}

export const searchService = new SearchService();
